import { Field } from '@state/regions/region.models';
import { createSelector } from 'reselect';
import type { BoundStore } from '../store';

export const selectFields = createSelector(
  (state: BoundStore) => state.regions.entities,
  (state: BoundStore) => state.seasons.entities,
  (state: BoundStore) => state.regions.selectedRegionId,
  (state: BoundStore) => state.seasons.selectedSeasonId,
  (state: BoundStore) => state.seasons.displayWithoutActiveSeason,
  (entities, seasonEntities, selectedRegionId, selectedSeasonId, withoutSeason) => {
    let fields: Field[] = [];
    const selectedSeason = seasonEntities[selectedSeasonId ?? ''];

    // 1. Pick which fields are being used
    if (selectedRegionId === 'root') {
      Object.values(entities).forEach((region) => {
        fields = fields.concat(Object.values(region.fields.entities));
      });
    } else {
      fields = Object.values(entities[selectedRegionId]?.fields.entities ?? []);
    }

    /**
     * For connect uploaded season, there is no need to filter fields based on crop.
     */
    if (selectedSeason?.uploaded) {
      return fields;
    }

    // 2. Filter by crop cycles (how did we get here)
    if (selectedSeason && withoutSeason) {
      fields = fields.filter((field) => {
        // Any field with no current crop shows under the "all" toggle, mirroring the map filter
        // (activeSeason === 'none'); historical associations don't disqualify it.
        const noSeason = !field.activeSeason;
        // For an active season only match the field's current crop; a field that grew this
        // crop but has since rotated (season now in inactiveSeasons) must not show here.
        if (selectedSeason.season.active) {
          return field.activeSeason?.season_id === selectedSeason.id || noSeason;
        }
        return field.inactiveSeasons.some((s) => s.season_id === selectedSeason.id) || noSeason;
      });
    } else if (selectedSeason && !withoutSeason) {
      if (selectedSeason.season.active) {
        fields = fields.filter((field) => {
          return field.activeSeason?.season_id === selectedSeason.id;
        });
      } else {
        fields = fields.filter((field) => {
          const seasonIds = field.inactiveSeasons.map((s) => s.season_id);
          return seasonIds.includes(selectedSeason.id);
        });
      }
    } else if (!selectedSeason && withoutSeason) {
      // Do nothing...
    } else if (!selectedSeason && !withoutSeason) {
      fields = fields.filter((field) => {
        return !!field.activeSeason;
      });
    }

    return fields;
  }
);

export const selectFieldsSorted = createSelector(selectFields, (fields) => {
  return fields.toSorted((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
  );
});

export const selectRegion = createSelector(
  (state: BoundStore) => state.regions.entities,
  (state: BoundStore) => state.regions.selectedRegionId,
  (entities, selectedRegionId) => {
    if (selectedRegionId === 'root') {
      return Object.values(entities)?.[0] ?? null;
    }
    return entities[selectedRegionId];
  }
);

export const selectField = createSelector(
  (state: BoundStore) => state.regions.entities,
  (state: BoundStore) => state.regions.selectedField,
  (entities, selected) => {
    if (selected) {
      const regionId =
        selected.regionId === 'root' ? Object.values(entities)[0].id : selected.regionId;

      if (regionId && entities[regionId]) {
        return entities[regionId].fields.entities[selected.id];
      }
    }
    return null;
  }
);

export const selectAllFields = createSelector(
  (state: BoundStore) => state.regions.entities,
  (entities) => {
    let fields: Field[] = [];
    Object.values(entities).forEach((region) => {
      fields = fields.concat(Object.values(region.fields.entities));
    });
    return fields;
  }
);

export const selectFieldFromProps = createSelector(
  (state: BoundStore) => state.regions.entities,
  (_state: BoundStore, fieldId?: string, regionId?: string | null) => ({
    fieldId,
    regionId,
  }),
  (entities, props): Field | null => {
    if (!props.fieldId || !props.regionId) return null;

    return entities[props.regionId]?.fields.entities[props.fieldId] ?? null;
  }
);
